/**
 * Route labels ("Hyderabad, Telangana → Guwahati, Assam"), one rule everywhere (#235, #136):
 * each end is "<City>, <State name>" when the state is known, else "<City>"; a blank city becomes
 * "Origin" / "Destination"; ends are joined with " → ". The console never shows a 2-letter state
 * code: a bare code from an older payload is expanded with the API's IndianState names.
 */
export interface RouteFields {
  originCity?: string | null;
  originState?: string | null;
  originStateName?: string | null;
  destinationCity?: string | null;
  destinationState?: string | null;
  destinationStateName?: string | null;
  routeLabel?: string | null;
}

/** Code → display name, copied from rhino-api IndianState (36 states/UTs). */
export const STATE_NAMES: Readonly<Record<string, string>> = {
  AP: 'Andhra Pradesh', AR: 'Arunachal Pradesh', AS: 'Assam', BR: 'Bihar', CG: 'Chhattisgarh', GA: 'Goa',
  GJ: 'Gujarat', HR: 'Haryana', HP: 'Himachal Pradesh', JH: 'Jharkhand', KA: 'Karnataka', KL: 'Kerala',
  MP: 'Madhya Pradesh', MH: 'Maharashtra', MN: 'Manipur', ML: 'Meghalaya', MZ: 'Mizoram', NL: 'Nagaland',
  OD: 'Odisha', PB: 'Punjab', RJ: 'Rajasthan', SK: 'Sikkim', TN: 'Tamil Nadu', TS: 'Telangana', TR: 'Tripura',
  UP: 'Uttar Pradesh', UK: 'Uttarakhand', WB: 'West Bengal', AN: 'Andaman & Nicobar', CH: 'Chandigarh',
  DD: 'Dadra & Nagar Haveli and Daman & Diu', DL: 'Delhi', JK: 'Jammu & Kashmir', LA: 'Ladakh',
  LD: 'Lakshadweep', PY: 'Puducherry',
};

export const ROUTE_ARROW = ' → ';

function clean(value?: string | null): string {
  return value?.trim() ?? '';
}

/** Full state name for a code ("TS" → "Telangana"); any other non-blank text is kept as given. */
export function stateDisplayName(state: string | null | undefined): string {
  const v = clean(state);
  return STATE_NAMES[v.toUpperCase()] ?? v;
}

/** One end of a route: "Pune, Maharashtra", "Pune" (state unknown) or the fallback when the city is blank. */
export function routeEnd(city: string | null | undefined, state: string | null | undefined, fallback: string): string {
  const name = clean(city) || fallback;
  const suffix = stateDisplayName(state);
  return suffix ? `${name}, ${suffix}` : name;
}

/**
 * The trip's from → to with full state names. The server `routeLabel` is used when present (it
 * carries full names since #136); otherwise it is built from the name fields, falling back to the
 * code expanded to its name. `long` is kept for callers; both forms now read the same.
 */
export function formatRoute(r: RouteFields, opts?: { long?: boolean }): string {
  const label = clean(r.routeLabel);
  if (label && !opts?.long) return label;
  return (
    routeEnd(r.originCity, clean(r.originStateName) || r.originState, 'Origin') +
    ROUTE_ARROW +
    routeEnd(r.destinationCity, clean(r.destinationStateName) || r.destinationState, 'Destination')
  );
}
